import { useRefreshTick } from "../lib/useAutoRefresh";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { FREQ_LABELS } from "../lib/format";
import { useAuth } from "../context/AuthContext";
import { useClientPerms } from "../lib/useClientPerms";
import { Building2, Plus, ArrowUpRight, Users } from "lucide-react";
import CompanyFormDialog from "../components/CompanyFormDialog";
import { DemoCompanyButton } from "../components/DemoCompanyButton";

export default function Companies() {
  const { user } = useAuth();
  const canCreate = useClientPerms().canEdit("companies_manage");
  const [companies, setCompanies] = useState([]);
  const [open, setOpen] = useState(false);

  const sortKey = (c) => String(c.name || "").replace(/[\u200B-\u200D\uFEFF\u00A0]/g, " ").trim().toLocaleLowerCase();
  const load = () => api.get("/companies").then((r) => setCompanies([...r.data].sort((a, b) => sortKey(a).localeCompare(sortKey(b), undefined, { numeric: true, sensitivity: "base" })))).catch(() => {});
  const tick = useRefreshTick();
  useEffect(() => { load(); }, [tick]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="w-full">
      <div className="mb-8 flex flex-wrap items-start justify-between gap-4">
        <div>
        <h1 className="text-4xl font-black tracking-tight">Client Companies</h1>
        <p className="text-sm text-muted-foreground mt-1 font-mono uppercase tracking-widest">
          {companies.length} account{companies.length !== 1 ? "s" : ""}
        </p>
        {user?.role === "super_admin" && canCreate && (
          <button data-testid="add-company-button" onClick={() => setOpen(true)} className="mt-4 inline-flex items-center gap-2 bg-black text-white px-5 py-3 text-sm font-medium hover:bg-neutral-800 transition-colors duration-200">
            <Plus className="h-4 w-4" /> New Company
          </button>
        )}
        </div>
        {user?.role === "super_admin" && (user?.staff_role || "master") === "master" && <DemoCompanyButton onChanged={load} />}
      </div>

      <CompanyFormDialog open={open} onOpenChange={setOpen} company={null} onSaved={load} />

      {companies.length ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {companies.map((c) => (
            <Link
              key={c.company_id}
              to={`/companies/${c.company_id}`}
              data-testid={`company-card-${c.company_id}`}
              className="group border border-border bg-white p-6 hover:border-black transition-colors duration-200"
            >
              <div className="flex items-start justify-between">
                {c.logo ? (
                  <img src={c.logo} alt={c.name} data-testid={`company-logo-${c.company_id}`} className="h-11 w-11 object-contain border border-border bg-white p-0.5" />
                ) : (
                  <div className="h-11 w-11 bg-black text-white flex items-center justify-center">
                    <Building2 className="h-5 w-5" />
                  </div>
                )}
                <ArrowUpRight className="h-5 w-5 text-muted-foreground group-hover:text-black transition-colors" />
              </div>
              <h3 className="mt-5 font-bold tracking-tight text-lg">{c.name}</h3>
              <p className="text-xs font-mono text-muted-foreground mt-1">{c.cra_business_number || "No BN on file"}</p>
              <div className="mt-4 flex items-center gap-4 text-xs font-mono uppercase tracking-widest text-muted-foreground border-t border-border pt-4">
                <span className="flex items-center gap-1.5"><Users className="h-3.5 w-3.5" /> {c.employee_count} emp</span>
                <span>{c.province}</span>
                <span>{FREQ_LABELS[c.default_pay_frequency]}</span>
              </div>
            </Link>
          ))}
        </div>
      ) : (
        <div className="border border-border bg-white px-6 py-20 text-center">
          <Building2 className="h-8 w-8 mx-auto text-muted-foreground" />
          <p className="mt-4 text-sm text-muted-foreground">No client companies yet. Create your first one to onboard employees.</p>
        </div>
      )}
    </div>
  );
}
